import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import * as Permissions from "../supportFunctions/Permissions";
import AddCustomer from "./Customer/AddCustomer";
import Customers from "./Customer/Customers";
import EditCustomer from "./Customer/EditCustomer";
import ManageHierarchies from "./Hierarchies/ManageHierarchies";
import EditOrgUnit from "./Hierarchies/EditOrgUnit";
import { AuthCheck } from "./AuthCheck";
import AddMetadata from "./Metadata/AddMetadata";
import EditMetadata from "./Metadata/EditMetadata";
import Metadatas from "./Metadata/Metadatas";
import PrivateRoute from "./PrivateRoute";
import AddSPMetadata from "./SPMetadata/AddSPMetadata";
import EditSPMetadata from "./SPMetadata/EditSPMetadata";
import SPMetadatas from "./SPMetadata/SPMetadatas";
import { Unauthorized, PageLayout } from "@velocity-ehs/component-library";
import Persons from "./Persons/Persons";
import { useTranslation } from "react-i18next";
import { useFlags } from "launchdarkly-react-client-sdk";
import AddPerson from "./Persons/AddPerson";
import EditPerson from "./Persons/EditPerson";
import IframeWrapper from "./HostedApps/IframeWrapper";
import SigmaReports from "./Reports/SigmaReports";
import SigmaDashboard from "./Dashboard/SigmaDashboard";
import EhsRoleManagement from "./EhsConfiguration/EhsRoleManagement";
import ManageEhsRole from "./EhsConfiguration/ManageEhsRole";
import EhsConfigurations from "./EhsConfiguration/EhsConfigurations";
import MyProfile from "./MyProfile/MyProfile";
import AddPersonUser from "./Persons/AddPersonUser/AddPersonUser";
import RoleManagement from "./RoleManagement/RolesByLocation";
import UsersAssignedToRole from "./RoleManagement/UsersAssignedToRole/UsersAssignedToRole";
import { RedirectToPersons } from "./UserManagement/RedirectToPersons";
import LocationsPageNew from "./LocationsNew/LocationsPageNew";
import ManageLocationNew from "./LocationsNew/ManageLocationNew";
import { Mode as LocationMode } from "./Locations/AddEditLocationForm";
import ContractingCompaniesList from "./ContractingCompanies/ContractingCompaniesList";
import ManageRole from "./RoleManagement/ManageRole/ManageRole";
import RolesPage from "./RoleManagement/Roles";
import LocationsManageApplications from "./LocationsNew/LocationsManageApplications";
import AddRole from "./RoleManagement/ManageRole/AddRole";
import EhsListsManagement from "./EhsConfiguration/EhsListsManagement";
import HomePageSelector from "./Home/HomePageSelector";

export enum Mode {
  ADD = "Add",
  EDIT = "Edit",
}

const RouterSwitch: React.FC = () => {
  const { t } = useTranslation();
  const {
    productPlatformMenuDashboardDisplay,
    productPlatformMenuReportsDisplay,
    showEHSRoles,
  } = useFlags();

  const history = useHistory();

  const fullPage = (node: JSX.Element, additionalClass?: string[]) => {
    return <PageLayout additionalClass={additionalClass}>{node}</PageLayout>;
  };

  const homeWrapper = (node: JSX.Element) => {
    return <PageLayout additionalClass={["home-content"]}>{node}</PageLayout>;
  };

  return (
    <Switch>
      <PrivateRoute
        exact
        path="/"
        title={`${t("universal_Home")} | Accelerate`}
        component={fullPage(<HomePageSelector />)}
      />
      <PrivateRoute
        exact
        path="/accelerate"
        title={`${t("universal_Home")} | Accelerate`}
        component={fullPage(<HomePageSelector />)}
      />

      <PrivateRoute
        exact
        path="/forbidden"
        title={`${t("universal_Home")} | Accelerate`}
        component={homeWrapper(<Unauthorized navigate={history.push} />)}
      />

      <PrivateRoute
        exact
        path="/singleSignOn"
        title={`${t("universal_Single_Sign_On")} | Accelerate`}
        component={fullPage(
          <Metadatas editPermission={[Permissions.canEditSSO]} />
        )}
        viewPermissions={[Permissions.canViewSSO, Permissions.canViewAdminMenu]}
      />
      <PrivateRoute
        exact
        path="/singleSignOn/add"
        title={`${t("action_add_single_sign_on")} | Accelerate`}
        component={fullPage(<AddMetadata />)}
        viewPermissions={[Permissions.canEditSSO, Permissions.canViewAdminMenu]}
      />
      <PrivateRoute
        path="/singleSignOn/:id/edit"
        title={`${t("universal_edit_single_signon")} | Accelerate`}
        component={fullPage(
          <EditMetadata
            editPermission={[
              Permissions.canEditSSO,
              Permissions.canViewAdminMenu,
            ]}
          />
        )}
        viewPermissions={[Permissions.canViewSSO]}
      />
      <PrivateRoute
        exact
        path="/applicationConfig"
        title={`${t("universal_Application_Config")} | Accelerate`}
        component={fullPage(<SPMetadatas />)}
        viewPermissions={[
          Permissions.canViewAppConfig,
          Permissions.canViewAdminMenu,
        ]}
      />
      <PrivateRoute
        exact
        path="/applicationConfig/add"
        title={`${t("action_add_application_config")} | Accelerate`}
        component={fullPage(<AddSPMetadata />)}
        viewPermissions={[
          Permissions.canEditAppConfig,
          Permissions.canViewAdminMenu,
        ]}
      />
      <PrivateRoute
        path="/applicationConfig/:id/edit"
        title={`${t(
          "application_Config_Application_Config_Text"
        )} | Accelerate`}
        component={fullPage(<EditSPMetadata />)}
        viewPermissions={[
          Permissions.canEditAppConfig,
          Permissions.canViewAdminMenu,
        ]}
      />

      <PrivateRoute
        exact
        title={`${t("universal_Customers")} | Accelerate`}
        path="/customer"
        component={fullPage(
          <Customers
            viewProfilePermission={[Permissions.canViewCustomers]}
            addPermission={[Permissions.canEditCustomers]}
          />
        )}
        viewPermissions={[
          Permissions.canViewCustomers,
          Permissions.canViewAdminMenu,
        ]}
      />
      <PrivateRoute
        exact
        path="/customer/add"
        title={`${t("universal_add_customers")} | Accelerate`}
        component={fullPage(<AddCustomer />)}
        viewPermissions={[
          Permissions.canEditCustomers,
          Permissions.canViewAdminMenu,
        ]}
      />
      <PrivateRoute
        path="/customer/:id/edit"
        title={`${t("universal_edit_customers")} | Accelerate`}
        component={fullPage(
          <EditCustomer editPermission={[Permissions.canEditCustomers]} />
        )}
        viewPermissions={[
          Permissions.canViewCustomers,
          Permissions.canViewAdminMenu,
        ]}
      />
      <PrivateRoute
        path="/user/:id/edit"
        title={`${t("action_edit_user")} | Accelerate`}
        component={fullPage(<RedirectToPersons />)}
        viewPermissions={[Permissions.canViewAdminMenu]}
      />
      <PrivateRoute
        path="/profile"
        title={`${t("universal_Profile")} | Accelerate`}
        component={fullPage(
          <MyProfile
            editPermission={[Permissions.canEditUsers]}
            manageUserPreferencesPermission={[
              Permissions.canManageUserPreferences,
            ]}
          />
        )}
      />

      <PrivateRoute
        exact
        path="/location"
        title={`${t("universal_Locations")} | Accelerate`}
        component={fullPage(<LocationsPageNew />)}
        viewPermissions={[
          Permissions.canManageLocations,
          Permissions.canViewLocationsMenu,
          Permissions.canViewAdminMenu,
        ]}
      />

      <PrivateRoute
        path="/location/:id/edit"
        title={`${t("universal_manage_location")} | Accelerate`}
        component={fullPage(<ManageLocationNew mode={LocationMode.Edit} />)}
        viewPermissions={[
          Permissions.canManageLocations,
          Permissions.canViewAdminMenu,
        ]}
      />

      <PrivateRoute
        path="/location/:id/add"
        title={`${t("universal_manage_location")} | Accelerate`}
        component={fullPage(<ManageLocationNew mode={LocationMode.Add} />)}
        viewPermissions={[
          Permissions.canManageLocations,
          Permissions.canViewAdminMenu,
        ]}
      />

      <PrivateRoute
        path="/location/:id/manage-applications"
        title={`${t("locations_manage_applications")} | Accelerate`}
        component={fullPage(<LocationsManageApplications />)}
        viewPermissions={[
          Permissions.canManageLocations,
          Permissions.canViewAdminMenu,
        ]}
      />

      <PrivateRoute
        path="/hierarchies"
        title={`${t("universal_Hierarchies")} | Accelerate`}
        component={fullPage(<ManageHierarchies />, ["flexRow flexDirCol"])}
        viewPermissions={[
          Permissions.canManageOrgHierarchies,
          Permissions.canViewHierarchiesMenu,
          Permissions.canViewAdminMenu,
        ]}
      />

      <PrivateRoute
        path="/orgUnit/:id/edit"
        title={`${t("universal_manage_hierarchies")} | Accelerate`}
        component={fullPage(<EditOrgUnit />)}
        viewPermissions={[
          Permissions.canManageOrgHierarchies,
          Permissions.canViewAdminMenu,
        ]}
      />

      <PrivateRoute
        exact
        path="/authcheck"
        title={`${t("universal_Home")} | Accelerate`}
        component={homeWrapper(<AuthCheck />)}
        viewPermissions={[Permissions.canViewMyProfile]}
      />

      <PrivateRoute
        exact
        path="/persons/addPerson"
        title={`${t("universal_add_person")} | Accelerate`}
        component={fullPage(<AddPerson />)}
        viewPermissions={[
          Permissions.canEditPersons,
          Permissions.canViewAdminMenu,
        ]}
      />

      <PrivateRoute
        exact
        path="/persons/addUser"
        title={`${t("universal_add_user")} | Accelerate`}
        component={fullPage(<AddPersonUser />)}
        viewPermissions={[
          Permissions.canEditPersons,
          Permissions.canViewAdminMenu,
        ]}
      />

      <PrivateRoute
        path="/persons"
        title={`${t("universal_Persons")} | Accelerate`}
        component={fullPage(
          <Persons editPermission={[Permissions.canEditPersons]} />
        )}
        viewPermissions={[Permissions.canViewAdminMenu]}
      />

      <PrivateRoute
        path="/person/:id/edit"
        title={`${t("universal_edit_person")} | Accelerate`}
        component={fullPage(
          <EditPerson editPermission={[Permissions.canEditPersons]} />
        )}
        viewPermissions={[Permissions.canViewAdminMenu]}
      />

      <Route
        exact
        path="/ehs-configurations"
        render={() => <Redirect to="/ehs-configurations/Task" />}
      />

      <PrivateRoute
        exact
        path="/ehs-configurations/:config"
        title={`EHS ${t("universal_configurations")} | Accelerate`}
        component={fullPage(<EhsConfigurations />)}
        viewPermissions={[
          Permissions.canViewEhsConfig,
          Permissions.canViewAdminMenu,
        ]}
      />

      <PrivateRoute
        path="/ehs-configurations/list-manager/lists"
        title={`EHS ${t("universal_configurations")} | Accelerate`}
        component={fullPage(<EhsListsManagement />)}
        viewPermissions={[
          Permissions.canViewEhsConfig,
          Permissions.canViewAdminMenu,
        ]}
      />

      <Route
        exact
        path="/role-management"
        render={() => <Redirect to="/role-management/all-roles" />}
      />

      <PrivateRoute
        exact
        path="/role-management/ehs-roles"
        title={`EHS ${t("meta_role_management")} | Accelerate`}
        component={() => (
          <EhsRoleManagement addPermission={[Permissions.canManageEhsRoles]} />
        )}
        viewPermissions={[
          Permissions.canManageEhsRoles,
          Permissions.canViewAdminMenu,
        ]}
        featureFlag={showEHSRoles}
      />

      <PrivateRoute
        exact
        path="/role-management/ehs-roles/add"
        title={`${t("action_add_role")} | Accelerate`}
        component={() => <ManageEhsRole mode={Mode.ADD} />}
        viewPermissions={[
          Permissions.canManageEhsRoles,
          Permissions.canViewAdminMenu,
        ]}
        featureFlag={showEHSRoles}
      />

      <PrivateRoute
        exact
        path="/role-management/ehs-roles/:id/edit"
        title={`${t("meta_edit_role")} | Accelerate`}
        component={() => <ManageEhsRole mode={Mode.EDIT} />}
        viewPermissions={[
          Permissions.canManageEhsRoles,
          Permissions.canViewAdminMenu,
        ]}
        featureFlag={showEHSRoles}
      />

      <PrivateRoute
        exact
        path="/role-management/all-roles"
        title={`${t("meta_role_management")} | Accelerate`}
        component={<RolesPage />}
        viewPermissions={[Permissions.canEditRole]}
      />

      <PrivateRoute
        exact
        path="/role-management/all-roles/add"
        title={`${t("meta_role_management")} | Accelerate`}
        component={<AddRole />}
        viewPermissions={[Permissions.canEditRole]}
      />

      <PrivateRoute
        path="/role-management/all-roles/:id/edit"
        title={`${t("meta_role_management")} | Accelerate`}
        component={<ManageRole />}
        viewPermissions={[Permissions.canEditRole]}
      />

      <PrivateRoute
        exact
        path="/role-management/location-roles"
        title={`${t("meta_role_management")} | Accelerate`}
        component={<RoleManagement />}
        viewPermissions={[Permissions.canViewAdminMenu]}
      />

      <PrivateRoute
        exact
        path="/role-management/location-roles/:id"
        title={`${t("meta_role_management")} | Accelerate`}
        component={() => <UsersAssignedToRole />}
        viewPermissions={[Permissions.canViewAdminMenu]}
      />

      <PrivateRoute
        exact
        path="/contracting-companies"
        title={`${t("universal_contracting_companies")} | Accelerate`}
        component={fullPage(<ContractingCompaniesList />)}
        viewPermissions={[Permissions.canViewContractingCompanies]}
      />

      <PrivateRoute
        exact
        path="/chemmanagement"
        title={`${t("universal_chem_management")} | Accelerate`}
        component={fullPage(
          <IframeWrapper
            hostedAppName="chem"
            appUrl={process.env.CHEM_MANAGEMENT_URL}
          />
        )}
      />

      <PrivateRoute
        path="/dashboard"
        title="Dashboard | Accelerate"
        component={fullPage(<SigmaDashboard />)}
        featureFlag={productPlatformMenuDashboardDisplay}
      />

      <PrivateRoute
        path="/reports"
        title="Reports | Accelerate"
        component={fullPage(<SigmaReports />)}
        featureFlag={productPlatformMenuReportsDisplay}
        viewPermissions={[Permissions.canExploreReportingData]}
      />

      {/* Redirect all 404's to the home page */}
      <Redirect to="/" />
    </Switch>
  );
};

export default RouterSwitch;
